/**
 * ALTIORA · Coach IA CSF (Sprint 3)
 *
 * Two tabs :
 *   1. Scripts   — generate a contextual sales script (product × stage)
 *   2. Roleplay  — chat simulator against a client persona
 */
import { useEffect, useState, useCallback, useRef } from "react";
import {
  MessageSquare, Wand2, Trash2, Send, RotateCw, RefreshCw, Mic, MicOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  DangerButton, FormField, Input, Textarea, Select, Loader, TabsShell,
  EmptyState, Badge,
} from "@/components/altiora/PremiumUI";


const PERSONAS = [
  { key: "prospect_sceptique",  label: "Sceptique · marié 45 ans, 2 enfants" },
  { key: "prospect_analytique", label: "Analytique · ingénieur 38 ans" },
  { key: "prospect_presse",     label: "Pressé · entrepreneur 50 ans" },
];


export default function CoachCsf() {
  const [tab, setTab] = useState("scripts");
  return (
    <PageShell testid="coach-csf-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Sprint 3 · Coach IA CSF"
          title="Préparez vos rencontres avec l'IA"
          subtitle="Générez un script contextuel ou entraînez-vous en roleplay face à un client virtuel."
          testid="coach-header"
        />

        <TabsShell
          testid="coach-tabs"
          tabs={[
            { id: "scripts",  label: "Scripts de vente", icon: Wand2 },
            { id: "roleplay", label: "Roleplay client",   icon: MessageSquare },
          ]}
          active={tab}
          onChange={setTab}
        />

        {tab === "scripts"  && <ScriptsTab />}
        {tab === "roleplay" && <RoleplayTab />}
      </div>
    </PageShell>
  );
}


// ── Scripts tab ────────────────────────────────────────────────────

function ScriptsTab() {
  const [products, setProducts] = useState([]);
  const [stages, setStages]   = useState([]);
  const [scripts, setScripts] = useState([]);
  const [form, setForm] = useState({ product_key: "", stage_key: "", context: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [cfg, list] = await Promise.all([
      api.get("/sales_os/config"),
      api.get("/coach_csf/scripts"),
    ]);
    setProducts(cfg.data.products || []);
    setStages(cfg.data.stages.filter(s => !s.is_won && !s.is_lost));
    setScripts(list.data || []);
    setForm(f => ({
      product_key: f.product_key || cfg.data.products?.[0]?.key || "",
      stage_key:   f.stage_key   || cfg.data.stages?.[0]?.key   || "",
      context:     f.context,
    }));
  }, []);

  useEffect(() => { load().catch(() => toast.error("Chargement impossible")); }, [load]);

  const generate = async () => {
    if (!form.product_key || !form.stage_key) {
      toast.error("Choisissez un produit et une étape"); return;
    }
    setBusy(true);
    try {
      await api.post("/coach_csf/script", form);
      toast.success("Script généré");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce script ?")) return;
    await api.delete(`/coach_csf/scripts/${id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Card className="p-4" testid="coach-generator-card">
        <Section title="Générer un script" hint="Adapté à votre produit et étape">
          <FormField label="Produit" required testid="coach-script-product">
            {({ id }) => (
              <Select id={id} value={form.product_key}
                      onChange={(e) => setForm({ ...form, product_key: e.target.value })}
                      testid="coach-script-product-select">
                {products.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </Select>
            )}
          </FormField>
          <FormField label="Étape" required testid="coach-script-stage">
            {({ id }) => (
              <Select id={id} value={form.stage_key}
                      onChange={(e) => setForm({ ...form, stage_key: e.target.value })}
                      testid="coach-script-stage-select">
                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </Select>
            )}
          </FormField>
          <FormField label="Contexte client" testid="coach-script-context"
                     description="Situation familiale, revenus, contraintes…">
            {({ id }) => (
              <Textarea id={id} rows={4} value={form.context}
                        onChange={(e) => setForm({ ...form, context: e.target.value })}
                        placeholder="Ex. Marié 40 ans, 2 enfants, revenu 90k, cherche protection famille"
                        testid="coach-script-context-input" />
            )}
          </FormField>
          <PrimaryButton onClick={generate} disabled={busy} testid="coach-script-generate">
            <Wand2 size={13} /> {busy ? "Génération…" : "Générer le script"}
          </PrimaryButton>
        </Section>
      </Card>

      <Card className="p-4" testid="coach-scripts-list-card">
        <Section title="Mes scripts sauvegardés" hint={`${scripts.length} au total`}>
          {scripts.length === 0 ? (
            <EmptyState icon={Wand2} title="Aucun script pour l'instant"
                        description="Générez votre premier script à gauche."
                        testid="coach-scripts-empty" />
          ) : (
            <ul className="space-y-3">
              {scripts.map(s => (
                <li key={s.id} data-testid={`coach-script-item-${s.id}`}
                    className="rounded-lg p-3 bg-white"
                    style={{ border: "1px solid #C5A88033" }}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-[13px] font-semibold"
                           style={{ color: "#0B2545" }}>{s.title}</div>
                      <div className="text-[10px]" style={{ color: "#94A3B8" }}>
                        {new Date(s.created_at).toLocaleDateString("fr-CA")}
                      </div>
                    </div>
                    <button onClick={() => remove(s.id)} aria-label="Supprimer"
                            data-testid={`coach-script-delete-${s.id}`}
                            className="p-1 rounded hover:bg-slate-100"
                            style={{ color: "#94A3B8" }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <pre className="text-[12px] whitespace-pre-wrap font-sans"
                       style={{ color: "#334155" }}>{s.content}</pre>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </Card>
    </div>
  );
}


// ── Roleplay tab ───────────────────────────────────────────────────

function RoleplayTab() {
  const [products, setProducts] = useState([]);
  const [stages,   setStages]   = useState([]);
  const [setup, setSetup] = useState({
    product_key: "", stage_key: "", persona: "prospect_sceptique",
  });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [session, setSession] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSpeechSupported(true);
    const rec = new SR();
    rec.lang = "fr-CA";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let finalTxt = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalTxt += r[0].transcript;
        else           interim   += r[0].transcript;
      }
      setDraft((prev) => (finalTxt ? (prev + " " + finalTxt).trim()
                                        : prev + interim).slice(0, 800));
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    return () => { try { rec.stop(); } catch {} };
  }, []);

  const toggleListen = () => {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) { rec.stop(); setListening(false); }
    else { try { rec.start(); setListening(true); } catch {} }
  };

  useEffect(() => {
    api.get("/sales_os/config").then(({ data }) => {
      setProducts(data.products || []);
      setStages(data.stages.filter(s => !s.is_won && !s.is_lost));
      setSetup(s => ({
        product_key: s.product_key || data.products?.[0]?.key || "",
        stage_key:   s.stage_key   || data.stages?.[0]?.key   || "",
        persona:     s.persona,
      }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const startSession = () => {
    if (!setup.product_key || !setup.stage_key) {
      toast.error("Choisissez produit et étape"); return;
    }
    setSession(`rp_${Date.now()}`);
    setMessages([{
      role: "system",
      text: `Session démarrée avec un client « ${
        PERSONAS.find(p => p.key === setup.persona)?.label
      } ». À vous de vous présenter.`,
    }]);
  };

  const send = async () => {
    if (!draft.trim() || !session) return;
    const advisorMsg = { role: "advisor", text: draft.trim() };
    const nextHist = [...messages, advisorMsg];
    setMessages(nextHist);
    setDraft("");
    setSending(true);
    try {
      const { data } = await api.post("/coach_csf/roleplay/send", {
        session_id: session,
        product_key: setup.product_key,
        stage_key: setup.stage_key,
        persona: setup.persona,
        history: nextHist.filter(m => m.role !== "system"),
      });
      setMessages([...nextHist, { role: "client", text: data.reply }]);
    } catch (err) {
      toast.error("Le client IA n'a pas répondu");
    } finally { setSending(false); }
  };

  const reset = () => { setSession(null); setMessages([]); setDraft(""); };

  return (
    <Card className="p-4" testid="coach-roleplay-card">
      <Section title="Simulateur client" hint="Entraînez-vous face à un persona configurable">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <FormField label="Produit" testid="coach-rp-product">
            {({ id }) => (
              <Select id={id} value={setup.product_key} disabled={!!session}
                      onChange={(e) => setSetup({ ...setup, product_key: e.target.value })}
                      testid="coach-rp-product-select">
                {products.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </Select>
            )}
          </FormField>
          <FormField label="Étape" testid="coach-rp-stage">
            {({ id }) => (
              <Select id={id} value={setup.stage_key} disabled={!!session}
                      onChange={(e) => setSetup({ ...setup, stage_key: e.target.value })}
                      testid="coach-rp-stage-select">
                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </Select>
            )}
          </FormField>
          <FormField label="Persona" testid="coach-rp-persona">
            {({ id }) => (
              <Select id={id} value={setup.persona} disabled={!!session}
                      onChange={(e) => setSetup({ ...setup, persona: e.target.value })}
                      testid="coach-rp-persona-select">
                {PERSONAS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </Select>
            )}
          </FormField>
        </div>

        {!session ? (
          <PrimaryButton onClick={startSession} testid="coach-rp-start">
            <MessageSquare size={13} /> Démarrer la simulation
          </PrimaryButton>
        ) : (
          <>
            <div ref={scrollRef}
                 data-testid="coach-rp-transcript"
                 className="rounded-lg p-3 mb-3 h-80 overflow-y-auto space-y-2"
                 style={{ background: "#FBF6EA", border: "1px solid #C5A88033" }}>
              {messages.map((m, i) => (
                <div key={i} data-testid={`coach-rp-msg-${i}`}
                     className={`flex ${m.role === "advisor" ? "justify-end" : "justify-start"}`}>
                  <div className="rounded-lg px-3 py-2 max-w-[75%] text-[12px]"
                       style={{
                         background: m.role === "advisor" ? "#0B2545"
                                   : m.role === "system"  ? "transparent" : "#fff",
                         color:      m.role === "advisor" ? "#fff"
                                   : m.role === "system"  ? "#94A3B8" : "#0B2545",
                         border: m.role === "client" ? "1px solid #C5A88033" : "none",
                         fontStyle: m.role === "system" ? "italic" : "normal",
                       }}>
                    {m.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="text-[11px] italic" style={{ color: "#94A3B8" }}>
                  Le client réfléchit…
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input value={draft}
                     onChange={(e) => setDraft(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                     placeholder={listening ? "Écoute en cours…" : "Tapez ou parlez votre réplique…"}
                     testid="coach-rp-input" />
              {speechSupported && (
                <GhostButton onClick={toggleListen} testid="coach-rp-mic">
                  {listening ? <MicOff size={13} /> : <Mic size={13} />}
                  {listening ? " Arrêter" : " Parler"}
                </GhostButton>
              )}
              <PrimaryButton onClick={send} disabled={sending || !draft.trim()}
                             testid="coach-rp-send">
                <Send size={13} /> Envoyer
              </PrimaryButton>
              <GhostButton onClick={reset} testid="coach-rp-reset">
                <RotateCw size={13} /> Réinitialiser
              </GhostButton>
            </div>
          </>
        )}
      </Section>
    </Card>
  );
}
